import { v4 as uuidv4 } from "uuid";
import { UploadedFile } from "../../../components/FileUploadSection";
import { Vehicle } from "../../../services/api";

export interface PetrolPump {
  id: string;
  petrolPumpName: string;
  primaryContactName: string;
  primaryContactNumber: string;
  providerName: string;
  location: string;
  bulkDiscountPerLitre: number;
}

// Expense types from API documentation
export const EXPENSE_TYPES = [
  "FUEL",
  "REPAIR",
  "DRIVER_CLEANER",
  "PARKING",
  "OTHER",
  "CHALLAN",
  "OUTSOURCED_OWNER",
  "PICKUP_MAN",
  "MINOR_REPAIR",
] as const;

export type ExpenseType = (typeof EXPENSE_TYPES)[number];

// Fuel types from API documentation
export const FUEL_TYPES = ["Petrol", "Diesel", "CNG", "Electric"] as const;
export type FuelType = (typeof FUEL_TYPES)[number];

// Service provider types
export const SERVICE_PROVIDER_TYPES = ["Garage", "Mechanic"] as const;
export type ServiceProviderType = (typeof SERVICE_PROVIDER_TYPES)[number];

// Action types for maintenance/repair
export const ACTION_TYPES = [
  "Repaired",
  "Replaced",
  "Serviced",
  "Inspected",
  "Adjusted",
  "Cleaned",
] as const;
export type ActionType = (typeof ACTION_TYPES)[number];

// Staff/Parking/Other expense categories from API documentation
export const STAFF_PARKING_CATEGORIES = [
  "DRIVER",
  "CLEANER",
  "PARKING",
  "TOLL",
  "RTO",
  "Challan",
  "Food Allowance",
  "Mobile Recharge",
  "Loading/Unloading Fees",
  "Night Halt Charges",
  "Emergency Fund Used",
  "Dhobi",
  "Bus Cleaning (Interior)",
  "OUTSOURCED_OWNER",
  "PICKUP_MAN",
  "Custom",
] as const;

export type StaffParkingCategory = (typeof STAFF_PARKING_CATEGORIES)[number];

// Generic expense categories (subset for UI grouping)
export const GENERIC_EXPENSE_CATEGORIES = [
  "TOLL",
  "RTO",
  "Challan",
  "Food Allowance",
  "Mobile Recharge",
  "Loading/Unloading Fees",
  "Night Halt Charges",
  "Emergency Fund Used",
  "Dhobi",
  "Bus Cleaning (Interior)",
  "Custom",
] as const;

// Staff categories for Driver/Cleaner expenses
export const STAFF_CATEGORIES = ["DRIVER", "CLEANER"] as const;

export type GenericExpenseCategory =
  (typeof GENERIC_EXPENSE_CATEGORIES)[number];
export type StaffCategory = (typeof STAFF_CATEGORIES)[number];

// Combined category type for all expense types
export type ExpenseCategory = GenericExpenseCategory | StaffCategory;

// Updated ExpenseEntry interface to match API structure
export interface ExpenseEntry {
  // Main expense fields (maps to VehicleExpenseData)
  id?: string; // For edit mode
  isNew?: boolean;
  isEdited?: boolean; // Flag to indicate if this is an edited version
  type: string; // Will be mapped to expenseType
  expenseDate: string;
  amount: string;
  paidTo: string;
  location: string;
  contactNumber: string;
  notes: string;

  description?: string;
  vehicleId?: string;

  // Payment details
  payments?: PaymentDetail[];
  deletedPayments?: PaymentDetail[];
  remainingAmount?: number;
  paymentErrors?: {
    hasRequiredFieldErrors: boolean;
    hasAmountError: boolean;
    totalPaidAmount: number;
    remainingAmount: number;
  };

  isMinorRepair?: boolean;

  // isManualEntry
  isManualEntry?: boolean;

  // Fuel-specific fields (maps to FuelExpenseDetail)
  fuelType: string;
  liters: string;
  pricePerLitre?: string;
  bulkDiscountApplied?: string;
  petrolPumpId?: string;
  odometerReading?: string;
  fuelCardId?: string;
  isFuelCard?: boolean; // Tracks if fuel card checkbox is checked

  // Maintenance/Repair specific fields (maps to MaintenanceExpenseDetail)
  serviceType: string;
  serviceProvider: string;
  serviceProviderType?: ServiceProviderType;
  serviceProviderId?: string;
  maintenanceItem?: string;
  customMaintenanceItem?: string;
  actionType?: ActionType;
  warrantyPeriod?: string;
  warrantyStartDate?: string;
  warrantyEndDate?: string;
  customWarrantyPeriod?: string;
  nextServiceDue?: string;
  invoiceNumber?: string;
  workOrderNumber?: string;
  gst?: number;
  // Staff/Parking/Other expense fields (maps to StaffParkingExpenseDetail)
  category?: StaffParkingCategory;
  referenceId?: string;
  customCategory?: string; // For when category is "Custom"

  // Challan expense fields
  responsibleParty?: ResponsiblePartyCategory;
  responsiblePartyName?: string;
  responsiblePartyContact?: string;
  responsiblePartyId?: string | null;
  challanType?: string;
  challanNumber?: string;
  challanAction: string;

  // Rental Booking expense fields
  isChanged?: boolean;
  isDeleted?: boolean;
  updatedAt?: string;
  deletedDetails?: any;

  // Files
  selectedFiles?: File[];
  uploadedFiles?: UploadedFile[];

  // Whatsapp
  sendWhatsappToDriver?: boolean;
  whatsappData?: {
    average: number;
    lastReadingDate: string;
    currentReadingDate: string;
    lastOdometerReading: number;
    drivers: {
      name: string;
      contactNumber: string;
    }[];
    vehicleNumber?: string;
  } | null;

  // Common fields
  masterReferenceId?: string | null;
  masterCategory?: string | null;
  companyId?: string | null;
  createdBy?: string | null;
}

export interface ExpenseFormData {
  vehicleNumber: string;
  vehicleId?: string;
  vehicle?: Vehicle | null;
  acquisitionType?: string;
  currentOdometerReadings?: any;
  expenses: ExpenseEntry[];
  odometerReadings?: Map<
    string,
    {
      index: number;
      odometerReading: number;
      fuelQuantity: number;
      readingDate: string;
    }
  >;
  advancePayment?: number;
  advancePaymentReferenceId?: string;
  advancePaymentPaidTo?: string;
  advancePaymentLocation?: string;
  advancePaymentMode?: string;
}

export interface BaseExpenseFieldsProps {
  index: number;
  isProjectExpense?: boolean;
  isTripExpense?: boolean;
}

// Helper function to get empty expense entry with proper defaults
export const getEmptyExpenseEntry = (
  type: string,
  companyId?: string | null,
  createdBy?: string | null,
  masterReferenceId?: string | null,
  masterCategory?: string | null,
  journeyStartDate?: string
): ExpenseEntry => ({
  id: uuidv4(),
  isNew: true,
  isEdited: false,
  isDeleted: false,
  type,
  expenseDate: journeyStartDate
    ? new Date(journeyStartDate).toISOString().split("T")[0]
    : new Date().toISOString().split("T")[0],
  amount: "",
  paidTo: "",
  location: "",
  contactNumber: "",
  notes: "",

  // Payment details
  payments: [],
  remainingAmount: 0,

  // Fuel fields
  fuelType: "",
  liters: "",
  pricePerLitre: "",
  bulkDiscountApplied: "",
  petrolPumpId: "",
  odometerReading: "",
  fuelCardId: "",
  isFuelCard: false,
  // Maintenance/Repair fields
  serviceType: "",
  serviceProvider: "",
  serviceProviderType: type === "Maintenance / Repair" ? "Garage" : undefined,
  serviceProviderId: "",
  maintenanceItem: "",
  customMaintenanceItem: "",
  actionType: undefined,
  warrantyPeriod: "",
  warrantyStartDate: "",
  warrantyEndDate: "",
  customWarrantyPeriod: "",
  nextServiceDue: "",
  invoiceNumber: "",
  workOrderNumber: "",
  gst: 0,
  // Staff/Parking/Other fields
  category:
    type === "Driver/Cleaner"
      ? "DRIVER"
      : type === "Parking"
      ? "PARKING"
      : type === "Outsourced Owner"
      ? "OUTSOURCED_OWNER"
      : type === "Pickup Man"
      ? "PICKUP_MAN"
      : type === "Other"
      ? "Custom"
      : undefined,
  referenceId: "",
  customCategory: "",

  // Challan expense fields
  responsibleParty: "DRIVER",
  responsiblePartyContact: "",
  challanType: "",
  responsiblePartyId: null,
  challanNumber: "",
  challanAction: "",

  // File
  selectedFiles: [],
  uploadedFiles: [],

  // Whatsapp
  sendWhatsappToDriver: false,
  whatsappData: null,

  // Common fields
  masterReferenceId,
  masterCategory,
  companyId,
  createdBy,
});

// Map expense type from UI format to API format
export const expenseTypeMap: Record<string, ExpenseType> = {
  Fuel: "FUEL",
  Maintenance: "REPAIR",
  Repair: "REPAIR",
  "Maintenance / Repair": "REPAIR",
  "Driver/Cleaner": "DRIVER_CLEANER",
  Parking: "PARKING",
  Challan: "CHALLAN",
  Other: "OTHER",
  "Outsourced Owner": "OUTSOURCED_OWNER",
  "Pickup Man": "PICKUP_MAN",
  "Minor Repair": "MINOR_REPAIR",
};

// Mapping functions to convert between UI and API formats
export const mapExpenseEntryToApiRequest = (
  entry: ExpenseEntry,
  vehicleNumber: string,
  vehicleId?: string
) => {
  const expenseData = {
    vehicleNumber,
    vehicleId,
    expenseDate: entry.expenseDate,
    expenseType: expenseTypeMap[entry.type] || "OTHER",
    amount: parseFloat(entry.amount) || 0,
    paidTo: entry.paidTo,
    location: entry.location,
    contactNumber: entry.contactNumber || undefined,
    notes: entry.notes || undefined,
    gst: Number(entry.gst) || 0,
    isEdited: entry.isEdited || false,
    isManualEntry: entry.isManualEntry || false,
    masterReferenceId: entry.masterReferenceId || null,
    masterCategory: entry.masterCategory || null,
    companyId: entry.companyId || null,
    ...(entry.id && { id: entry.id }),
  };

  const paymentData =
    entry.payments?.map((payment) => ({
      type: payment.type,
      mode: payment.mode,
      amount:
        typeof payment.amount === "string"
          ? parseFloat(payment.amount)
          : payment.amount,
      date: payment.date,
      location: payment.location,
      receivedFrom: payment.receivedFrom,
      remarks: payment.remarks,
      status: payment.status || "PENDING",
      isEdited: payment.isEdited || false,
      isDeleted: payment.isDeleted || false,
      ...(payment.id && { id: payment.id }),
    })) || [];

  let detailData: any = {};

  // Map detail data based on expense type
  switch (expenseTypeMap[entry.type]) {
    case "FUEL":
      detailData = {
        fuelType: entry.fuelType as FuelType,
        liters: parseFloat(entry.liters) || 0,
        pricePerLitre: parseFloat(entry.pricePerLitre || "0") || 0,
        bulkDiscountApplied:
          parseFloat(entry.bulkDiscountApplied || "0") || undefined,
        petrolPumpId: entry.petrolPumpId || undefined,
        odometerReading: parseInt(entry.odometerReading || "0") || undefined,
        fuelCardId: entry.fuelCardId || null,
        sendWhatsappToDriver: entry.sendWhatsappToDriver || false,
        whatsappData: entry.whatsappData || null,
      };
      break;

    case "REPAIR":
    case "MINOR_REPAIR":
      detailData = {
        serviceType: entry.serviceType || undefined,
        serviceProviderType: entry.serviceProviderType || "Garage",
        serviceProviderId: entry.serviceProviderId || undefined,
        maintenanceItem: entry.maintenanceItem || undefined,
        customMaintenanceItem: entry.customMaintenanceItem || undefined,
        actionType: entry.actionType || undefined,
        warrantyPeriod: entry.warrantyPeriod || undefined,
        warrantyStartDate: entry.warrantyStartDate || undefined,
        warrantyEndDate: entry.warrantyEndDate || undefined,
        customWarrantyPeriod: entry.customWarrantyPeriod || undefined,
        nextServiceDue: parseInt(entry.nextServiceDue || "0") || undefined,
        invoiceNumber: entry.invoiceNumber || undefined,
        workOrderNumber: entry.workOrderNumber || undefined,
        odometerReading: parseInt(entry.odometerReading || "0") || undefined,
      };
      break;

    case "DRIVER_CLEANER":
    case "PARKING":
    case "OTHER":
    case "OUTSOURCED_OWNER":
    case "PICKUP_MAN":
      detailData = {
        category: entry.category,
        referenceId: entry.referenceId || undefined,
        customCategory: entry.customCategory || undefined,
      };
      break;

    case "CHALLAN":
      detailData = {
        responsibleParty: entry.responsibleParty as ResponsiblePartyCategory,
        responsiblePartyName: entry.responsiblePartyName || undefined,
        responsiblePartyContact: entry.responsiblePartyContact || undefined,
        responsiblePartyId: entry.responsiblePartyId || null,
        challanType: entry.challanType || undefined,
        challanNumber: entry.challanNumber || undefined,
        challanAction: entry.challanAction || undefined,
      };
  }

  return {
    expenseData,
    detailData,
    paymentData,
  };
};

// Payment types and interfaces
export const PAYMENT_MODES = [
  "CASH",
  "BANK_TRANSFER",
  "UPI",
  "CHEQUE",
  "ONLINE",
  "OTHER",
  "FUEL_CARD",
] as const;
export type PaymentMode = (typeof PAYMENT_MODES)[number];

export const PAYMENT_TYPES = ["ADVANCE", "SETTLEMENT"] as const;
export type PaymentType = (typeof PAYMENT_TYPES)[number];

export const PAYMENT_STATUS = ["PENDING", "COMPLETED", "CANCELLED"] as const;
export type PaymentStatus = (typeof PAYMENT_STATUS)[number];

export interface PaymentDetail {
  id?: string;
  type: string;
  mode: string;
  amount: number;
  date: string;
  location: string;
  receivedFrom: string;
  remarks?: string;
  status: string;
  createdAt?: string;
  updatedAt?: string;
  deletedAt?: string;
  isEdited?: boolean;
  isDeleted?: boolean;
  isNew?: boolean;
  rentalJourneyExpenseId?: string | null;
  masterExpenseId?: string | null;
}

export const RESPONSIBLE_PARTY_CATEGORIES = ["DRIVER", "OFFICE"] as const;
export type ResponsiblePartyCategory =
  (typeof RESPONSIBLE_PARTY_CATEGORIES)[number];

export const hasOtherExpense = (expenses: ExpenseEntry[]) => {
  return expenses.some((expense) => expense.type === "Other");
};

export const displayErrorToast = (
  errors: any,
  showToast: (message: string, type: "success" | "error") => void
) => {
  // Helper function to find the first string error in nested objects
  const findFirstStringError = (obj: any): string | null => {
    if (!obj || typeof obj !== "object") return null;

    for (const key in obj) {
      const value = obj[key];

      if (typeof value === "string") {
        return value;
      } else if (Array.isArray(value)) {
        // Handle array of objects (like expenses)
        for (const item of value) {
          if (item && typeof item === "object") {
            const nestedError = findFirstStringError(item);
            if (nestedError) return nestedError;
          }
        }
      } else if (value && typeof value === "object") {
        // Handle nested objects (like payments)
        const nestedError = findFirstStringError(value);
        if (nestedError) return nestedError;
      }
    }

    return null;
  };

  // Check if there are validation errors and show toast for the first one
  if (errors && Object.keys(errors).length > 0) {
    const firstError = findFirstStringError(errors);
    if (firstError) {
      showToast(firstError, "error");
      return;
    }
  }
};
